import {
  FULFILLMENT_METHODS,
  LIVE_GOODS_DEPARTMENT,
  LIVE_GOODS_CLASS,
  COVID_19_CUTOFF_TIME,
  MAJOR_APPLIANCE
} from '../constants';
import { getStateName } from './StateCodes';

export const formatDate = (date, isLongMonthName) => {
  if (!date) {
    return date;
  }
  let formattedDate;
  if (typeof date === 'object') {
    formattedDate = date;
  } else if (typeof date === 'string' && date.indexOf('T') > -1) {
    formattedDate = new Date(date);
  } else {
    const dateparts = date.split('-');
    formattedDate = new Date(dateparts[0], dateparts[1] - 1, dateparts[2], 0, 0, 0);
  }

  if (formattedDate.toString() === 'Invalid Date') {
    return null;
  }
  const longMonthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  if (isLongMonthName) {
    return longMonthNames[formattedDate.getMonth()] + ' ' + formattedDate.getDate();
  }
  return shortMonthNames[formattedDate.getMonth()] + ' ' + formattedDate.getDate();
};

export const formatWeek = (date, isLongDayName) => {
  if (!date) {
    return date;
  }
  let formattedDate;
  if (typeof date === 'object') {
    formattedDate = date;
  } else if (typeof date === 'string' && date.indexOf('T') > -1) {
    formattedDate = new Date(date);
  } else {
    const dateparts = date.split('-');
    formattedDate = new Date(dateparts[0], dateparts[1] - 1, dateparts[2], 0, 0, 0);
  }
  if (formattedDate.toString() === 'Invalid Date') {
    return null;
  }
  const longWeekNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  const shortWeekNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (isLongDayName) {
    return longWeekNames[formattedDate.getDay()];
  }
  return shortWeekNames[formattedDate.getDay()];
};

export const safelyGet = (input, fallback) => {
  try {
    return input();
  } catch (exception) {
    return fallback;
  }
};

export const showTodayOrTomorrow = (date) => {
  if (!date) {
    return date;
  }
  const dateparts = date.split('-');
  const estimatShippingMonth = dateparts[1];
  const estimatShippingDay = dateparts[2];
  const todaysDate = new Date();
  const todayWeek = todaysDate.getDate();
  const todayMonth = todaysDate.getMonth();
  if (todayWeek === estimatShippingDay && (todayMonth + 1) === estimatShippingMonth) {
    return 'Today';
  }
  if ((todayWeek + 1) === estimatShippingDay && (todayMonth + 1) === estimatShippingMonth) {
    return 'Tomorrow';
  }
  return formatWeek(date, true);
};

export const getParsedFulfillment = (fulfillment, shippingServiceModel) => {
  if (shippingServiceModel) {
    const {
      dynamicETA: { totalHours, totalMinutes },
      endDate,
      startDate,
      freeShippingThreshold,
      deliveryTimeline,
      totalCharge,
      isBodfsShipMode,
      hasFreeShipping,
      locations
    } = shippingServiceModel || {};
    const shippingData = {
      ...fulfillment,
      deliveryTimeline,
      deliveryCharge: totalCharge,
      isBodfsShipMode,
      freeDeliveryThreshold: freeShippingThreshold,
      hasFreeShipping,
      locations,
      deliveryDates: {
        ...fulfillment.deliveryDates,
        startDate,
        endDate
      },
      dynamicEta: {
        ...fulfillment.dynamicETA,
        hours: totalHours,
        minutes: Number.isNaN(totalMinutes) ? '' : totalMinutes
      }
    };
    return shippingData;
  }
  return fulfillment;
};

export const isStoreOnlyWithClearance = (fulfillment, type) => {
  return fulfillment?.anchorStoreStatusType?.toUpperCase() === 'CLEARANCE' && type === 'Store Only';
};

export const isOutOfStockOnline = (fulfillment) => {
  const { locations = [] } = fulfillment;
  if (locations.length) {
    return locations[0]?.inventory?.isOutOfStock;
  }
  return false;
};

export const getNearByStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  if (locations.length > 1) {
    const nearbyStore = locations.find((location) => !location.isAnchor);
    return nearbyStore;
  }
  return null;
};
export const getLocalStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  const localStore = locations.find((location) => location.isAnchor);
  return localStore;
};

export const getOnlineLocation = (fulfillment) => {
  return (fulfillment?.locations || []).find((location) => location.type === 'online');
};

export const isBOSSBackordered = (fulfillment) => {
  return fulfillment?.backordered;
};

export const isBOSSOutOfStock = (fulfillment) => {
  const bossLocation = (fulfillment?.locations || []).find((location) => location.type === 'online');
  return bossLocation?.inventory?.isOutOfStock;
};

export const isBopisOutOfStock = (localStoreLocation, nearByStoreLocation) => {
  const localStoreOOS = localStoreLocation?.inventory?.isOutOfStock;
  const nearByStoreOOS = nearByStoreLocation?.inventory?.isOutOfStock;
  if (localStoreOOS || (localStoreOOS && nearByStoreOOS)) {
    return true;
  }
  return false;
};

export const isBOPISUnavailable = (localStoreLocation) => {
  const localStoreUnavailable = localStoreLocation?.inventory?.isUnavailable;
  return localStoreUnavailable;
};

export const isBOSSDominant = (bossFulfillment, location, quantity) => {

  if (bossFulfillment
    && location?.inventory?.quantity > 0
    && location.inventory.quantity < quantity) {
    return true;
  }
  return false;
};

export const isCovidAlertEnabled = (covidAlert, bopisHolidayTiming) => {
  return covidAlert && new Date().getHours() >= bopisHolidayTiming;
};

export const getExcludedStateName = (stateCode) => {
  return getStateName(stateCode) || stateCode;
};

export const isLocationInExcludedStates = (stateCode, excludedStates = '') => {
  const excludedStatesArray = excludedStates?.split(',') || '';
  return (stateCode && stateCode.length === 2 && excludedStatesArray.includes(stateCode));
};

export const getFulfillmentLabel = (fulfillment) => {
  if (fulfillment.method === FULFILLMENT_METHODS.STH) {
    return 'Ship to Home';
  }
  if (fulfillment.method === FULFILLMENT_METHODS.BOPIS) {
    return 'Store Pickup';
  }
  if (fulfillment.method === FULFILLMENT_METHODS.STORE) {
    return 'Ship to Store';
  }
  if (fulfillment.method === FULFILLMENT_METHODS.BODFS) {
    return 'Express Delivery';
  }
  return null;
};

export const getLiveGoodsLocations = (fulfillment) => {
  return safelyGet(() => (fulfillment.fulfillmentOptions)
    .find((option) => (option.type === 'pickup' && option.fulfillable === false
      && option.services)).services
    .find((services) => (services.type === 'bopis' && services.locations)).locations);
};

export const getLiveGoodsStock = (fulfillment) => {
  if (fulfillment?.seasonStatusEligible == null) {
    return false;
  }

  const bopisLocations = getLiveGoodsLocations(fulfillment);

  if (bopisLocations) {
    return safelyGet(() => bopisLocations
      .find((location) => (location.isAnchor)).inventory.quantity, 0) || 0;
  }
  return false;
};

export const isQuantityLimitExceed = ({ method, storeObj, shippingObj }) => {
  if (method === FULFILLMENT_METHODS.BOPIS || method === FULFILLMENT_METHODS.STORE) {
    return storeObj.quantityLimitExceed;
  }
  if (method === FULFILLMENT_METHODS.STH) {
    return shippingObj.quantityLimitExceed;
  }
  return false;
};

export const isStoreOnlyOOS = (fulfillment, type) => {
  const pickupService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');

  const localStoreLocation = getLocalStore(bopisFulfillment);
  const nearByStoreLocation = getNearByStore(bopisFulfillment);
  return ((isBopisOutOfStock(localStoreLocation, nearByStoreLocation) && type === 'Store Only'));
};

export const bopis = (fulfillment) => {
  const pickupService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bopisFulfillment = (pickupService?.services || []).find((service) => service.type === 'bopis');
  return bopisFulfillment || null;
};
export const sth = (fulfillment) => {
  const deliveryService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'delivery');
  const sthFulfillment = (deliveryService?.services || []).find((service) => service.type === 'sth');
  return sthFulfillment || null;
};
export const boss = (fulfillment) => {
  const pickupService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bossFulfillment = (pickupService?.services || []).find((service) => service.type === 'boss');
  return bossFulfillment || null;
};

export const isNoBOPISForAppliance = (productType, fulfillment) => {
  const bopisFulfillment = bopis(fulfillment);
  if (!bopisFulfillment && productType === MAJOR_APPLIANCE) {
    return true;
  }
  const localStoreLocation = getLocalStore(bopisFulfillment);
  return isBopisOutOfStock(localStoreLocation) && productType === MAJOR_APPLIANCE;
};

export const hasNoDirectDeliveryAvailable = ({ productType, fulfillment, checkAvailabilityModel }) => {
  const noBOPIS = isNoBOPISForAppliance(productType, fulfillment);
  return (checkAvailabilityModel?.hasOutOfStock || checkAvailabilityModel?.isUnavailable) && noBOPIS;
};

export const setDETAZip = (zipCode) => {
  if (typeof window !== 'undefined' && window.cookieUtils.fed_CreateCookie) {
    cookieUtils.fed_CreateCookie('DETA_ZIP', zipCode, null);
  }
};
export const getDETAZip = () => {
  return (typeof window !== 'undefined' && window?.cookieUtils?.readBrowserCookie('DETA_ZIP'));
};
export const getDeliveryZip = () => {
  let zipCode = '';
  if (typeof window !== 'undefined') {
    const zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    zipCode = zipDetails?.zipcode;
  }
  return zipCode;
};
export const setDeliveryZip = (deliveryZipCode) => {
  if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer && deliveryZipCode) {
    window.THD_LOCALIZER_AUTO_INIT.Localizer.updateDeliveryZipcodeDetails(deliveryZipCode, 'USER');
  }
};

export const formatInventory = (inventoryValue) => {
  let inventorySplit = inventoryValue.toLocaleString();
  return inventorySplit;
};

export const getLiveGoodsStoreName = (product) => {
  const bopisLocations = getLiveGoodsLocations(product?.fulfillment);

  if (bopisLocations) {
    return safelyGet(() => bopisLocations
      .find((location) => (location.isAnchor)).storeName) || false;
  }
  return false;
};
